<template>
  <div
    v-if="variant === 'extensive'"
    class="d-flex align-items-center justify-space-between">
    <div>
      <h3 class="text-h2 mb-2">
        <v-skeleton-loader
          v-if="isLoading"
          type="text"
          width="120" />
        <span v-else>
          {{ displayValue }}
        </span>
      </h3>
      <div
        v-if="displaySubValue && !isLoading"
        class="text-body-1 text-medium-emphasis mt-n2 mb-2">
        {{ displaySubValue }}
      </div>
      <span class="text-medium-emphasis">{{ config.label }}</span>
    </div>
    <span
      v-if="config.icon"
      class="d-flex align-center">
      <v-tooltip
        :text="config.label"
        location="top">
        <template #activator="{ props }">
          <component
            :is="config.icon"
            v-bind="props"
            class="text-secondary"
            size="50"
            stroke-width="1.5" />
        </template>
      </v-tooltip>
    </span>
  </div>
  <template v-else>
    <span
      v-if="config.icon"
      class="metric-icon d-flex align-center">
      <v-tooltip
        :text="config.label"
        location="top">
        <template #activator="{ props }">
          <component
            :is="config.icon"
            v-bind="props"
            class="text-secondary"
            size="30"
            stroke-width="1.5" />
        </template>
      </v-tooltip>
    </span>
    <div class="metric-value-wrapper">
      <v-skeleton-loader
        v-if="isLoading"
        type="text"
        width="100" />
      <template v-else>
        <span class="metric-value">
          {{ displayValue }}
        </span>
        <div
          v-if="displaySubValue"
          class="text-body-2 text-medium-emphasis">
          {{ displaySubValue }}
        </div>
      </template>
    </div>
  </template>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { MetricDisplayProps } from '@/models/metricDefinitions';
import { formatSubValue, formatValue, formatValueWithTarget } from '@/utils/formatters';

interface Props extends MetricDisplayProps {
  variant?: 'compact' | 'extensive';
}

const { value, target, subValue, config, isLoading = false, variant = 'compact' } = defineProps<Props>();

const displayValue = computed(() => {
  if (target !== undefined) {
    return formatValueWithTarget(value, target, config);
  }
  return formatValue(value, config);
});

const displaySubValue = computed(() => (subValue === undefined ? undefined : formatSubValue(subValue, config)));
</script>

<style scoped>
/* The icon keeps its width so it stays on the column edge, the value takes whatever is left. */
.metric-icon {
  flex: 0 0 auto;
}

.metric-value-wrapper {
  min-width: 0;
}

.metric-value {
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.25;
}
</style>
